/**
 * Cupom de FECHAMENTO de Mesa — fundo branco e texto preto (TM-T20X).
 * Mesa N, Comanda, Código (se houver), data/hora, tempo de permanência, itens (merged dos pedidos), total, pagamento, cliente.
 */
import { smartPrint } from "./printConnector";
import { carregarFormasDaLoja, nomeFormaAgora } from "../features/pedidos/lib/formasDaLoja";
import { fmtMoeda, fmtCnpj, formatCupomDate, formatCupomTime, buildLogoUrl, buildLogoHtml, getEmpresaData, getImpressaoConfig, buildEmpresaPayload, buildFontScaleStyle, mesaLabelPrint, getAllProductsCached, partesDoPagamento } from "./cupomFormatters";
import { contaDoCupomMesa, contaParaServidor } from "../features/mesas-fechamento/lib/contaDoCupom";
import { itensHtmlDoCupom } from "../features/mesas-fechamento/lib/itensHtmlCupom";

/** Tempo de permanência: opened_at até closed_at (ou "agora" se mesa ainda aberta). */
function formatPermanencia(openedAt, closedAt) {
  if (!openedAt) return "—";
  const fim = closedAt ? new Date(closedAt) : new Date();
  const min = (fim - new Date(openedAt)) / 60000;
  if (min < 1) return "< 1 min";
  if (min < 60) return `${Math.round(min)} min`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

/**
 * Imprime o cupom de FECHAMENTO da mesa (conta).
 * Inclui: Mesa, Comanda, Código (se houver), data/hora, tempo de permanência, itens, total, pagamento e cliente.
 * @param {Object} mesa - Mesa (numero, comanda, cliente, codigo, total, opened_at, closed_at)
 * @param {Array} pedidos - Pedidos da mesa (com items) já obtidos
 * @param {string} [formaPagamentoOverride] - Se informado, usa no lugar de forma_pagamento do último pedido (ex.: ao fechar mesa)
 * @param {Array} [pagamentosLog] - Lista de pagamentos da sessao [{label, code, value, troco_precisa, troco_valor, cash_received}]. Se passado, usa no lugar de lastPedido.pagamentos.
 * @param {Array} [produtos] - Cadastro de produtos (quilo/litro). Sem ele, busca com cache (getAllProductsCached).
 * @param {Object} [opcoes] - { descontoRodada, descontoAcumulado, agrupar } — ver `contaDoCupomMesa`; { automatico } = fluxo de fechar a mesa/pagar (respeita a chave "cupom de fechamento" do módulo; botão manual não manda). Sem log, os pagamentos vêm de `mesa.pagamentos`.
 */
export async function imprimirCupomFechamento(mesa, pedidos, formaPagamentoOverride, pagamentosLog, produtos, opcoes = {}) {
  // Ordenar por created_at e usar o PRIMEIRO pedido como referencia (mesmo codigo do cupom de preparo)
  const pedidosOrdenados = [...(pedidos || [])].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const firstPedido = pedidosOrdenados[0] || null;
  const lastPedido = pedidosOrdenados[pedidosOrdenados.length - 1] || firstPedido;

  // A conta (itens, grupos por pessoa, descontos, total líquido e pagamentos) vem
  // de um lugar só, o mesmo que o texto do servidor imprime (conta_cupom).
  const conta = contaDoCupomMesa(mesa, pedidos, {
    ...opcoes,
    log: pagamentosLog,
    logPadrao: partesDoPagamento(lastPedido),
  });
  const mergedItems = conta.itens;
  const totalFinal = conta.total;
  // Código "dinheiro_<loja>" é interno: no papel vai o nome. O cadastro vem junto:
  // o item gravado não guarda a unidade de venda — "1,250 kg x 59,90/kg" sai
  // pelo produto (30/09/2026; antes o fechamento não olhava o cadastro)
  const [, produtosCadastro] = await Promise.all([
    carregarFormasDaLoja(),
    Array.isArray(produtos) && produtos.length ? produtos : getAllProductsCached().catch(() => []),
  ]);
  const nomesPagos = [...new Set(conta.pagamentos.map((p) => (p.label || nomeFormaAgora(p.method) || "").trim()).filter(Boolean))];
  const overrideValido = typeof formaPagamentoOverride === "string" && formaPagamentoOverride.trim() !== "" && formaPagamentoOverride.trim() !== "Pagamento por produtos";
  const formaPagamento = overrideValido
    ? nomeFormaAgora(formaPagamentoOverride.trim())
    : nomesPagos.length > 0 ? nomesPagos.join(" + ")
    : (lastPedido?.forma_pagamento ? nomeFormaAgora(lastPedido.forma_pagamento) : "—");
  const created_at = (pedidos || [])[0]?.created_at || mesa.closed_at || new Date().toISOString();

  let company = { company_name: "Núcleo", slogan: "", address: "", cnpj: "", logo_url: null, phone: "" };
  const companyData = await getEmpresaData();
  if (companyData) company = { ...company, ...companyData };
  const impressaoConfig = await getImpressaoConfig();

  const nomeEmpresa = company.company_name || company.fantasy_name || "Núcleo";
  const logoUrl = buildLogoUrl(company);
  const logoHtml = buildLogoHtml(logoUrl);

  // Itens: com 2+ pessoas, um bloco por pessoa ("-- Camila --" … "SUBTOTAL CAMILA")
  const linhaDupla = (esq, dir, extra = "") => `<div style="display:flex;justify-content:space-between;font-weight:bold;${extra}"><span>${esq}</span><span>${dir}</span></div>`;
  let itensHtml = "";
  for (const g of conta.grupos) {
    const blocos = itensHtmlDoCupom(g.itens, produtosCadastro);
    if (!conta.agrupado) { itensHtml += blocos; continue; }
    itensHtml += `<div class="center" style="font-weight:bold;margin-top:4px;">-- ${g.rotulo} --</div>${blocos}`;
    // pontos entre o nome e o valor (span do meio com borda pontilhada; o conversor ESC/POS entende)
    itensHtml += `<div style="display:flex;justify-content:space-between;align-items:flex-end;font-weight:bold;margin-bottom:4px;"><span>SUBTOTAL ${g.rotulo.toUpperCase()}</span><span style="flex:1;border-bottom:1px dotted #000;margin:0 4px 3px;"></span><span>${fmtMoeda(g.subtotal)}</span></div>`;
  }
  if (!itensHtml) itensHtml = '<div style="font-size:12px;font-weight:bold;color:#333;">Nenhum item</div>';

  // Label do header: usa mesa.nome inteiro quando definido (ex: 'Mesa 5F'), fallback pra 'MESA NN'
  const mesaNomeTrim = (mesa.nome && String(mesa.nome).trim()) ? String(mesa.nome).trim() : null;
  const mesaHeader = mesaNomeTrim || `MESA ${String(mesa.numero ?? "").padStart(2, "0")}`;
  // Usado pro <title> da pagina HTML (precisa ser curto)
  const mesaNum = mesaLabelPrint(mesa.nome, mesa.numero);
  const comanda = mesa.comanda != null && String(mesa.comanda).trim() !== "" ? String(mesa.comanda).trim() : null;

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Fechamento Mesa ${mesaNum}</title>
<style>@page{size:80mm auto;margin:3mm;}body{margin:0;padding:8px;font-family:"Courier New",monospace;font-size:13px;font-weight:bold;background:none;color:#000;}.line{border-bottom:1px dashed #000;margin:6px 0;}.center{text-align:center;}.section{color:#000;font-weight:bold;margin:4px 0;}@media print{body{max-width:72.1mm;margin:0 auto;background:none;color:#000;}}${buildFontScaleStyle(impressaoConfig.font_scale)}</style>
</head><body style="max-width:72.1mm;margin:0 auto;background:none;color:#000;">
${logoHtml}
<div class="center" style="font-weight:bold;font-size:16px;">${nomeEmpresa}</div>
${(company.slogan||"").trim()?`<div class="center" style="font-size:11px;font-style:italic;color:#333;margin-bottom:4px;">${company.slogan}</div>`:""}
${(company.address||"").trim()?`<div class="center" style="font-size:12px;font-weight:bold;margin-bottom:4px;">${company.address}</div>`:""}
${(company.cnpj||"").trim()?`<div class="center" style="font-size:12px;font-weight:bold;">CNPJ: ${fmtCnpj(company.cnpj)}</div>`:""}
<div class="line"></div>
<div class="center section">— FECHAMENTO ${mesaHeader} —</div>
${comanda?`<div class="center" style="font-size:13px;font-weight:bold;">Comanda #${comanda}</div>`:""}
${(mesa.codigo != null && String(mesa.codigo).trim() !== "")?`<div class="center" style="font-size:12px;font-weight:bold;">Código: ${String(mesa.codigo).trim()}</div>`:""}
<div class="center" style="font-size:13px;font-weight:bold;">${formatCupomDate(created_at)}, ${formatCupomTime(created_at)}</div>
<div class="center" style="font-size:13px;font-weight:bold;">TEMPO DE PERMANÊNCIA: ${formatPermanencia(mesa.opened_at, mesa.closed_at)}</div>
<div class="line"></div>
<div class="center section">— ITENS —</div>
<div style="display:flex;justify-content:space-between;font-size:12px;font-weight:bold;color:#333;margin-bottom:4px;"><span>QTD PREÇO ITEM</span><span>TOTAL</span></div>
${itensHtml}
<div class="line"></div>
${conta.descontos.length > 0 ? linhaDupla("Subtotal:", `R$ ${fmtMoeda(conta.subtotal)}`) + conta.descontos.map((d) => linhaDupla(`${d.rotulo}:`, `- R$ ${fmtMoeda(d.valor)}`)).join("") : ""}
<div style="display:flex;justify-content:space-between;font-weight:bold;font-size:15px;">TOTAL:<span>R$ ${fmtMoeda(totalFinal)}</span></div>
<div class="line"></div>
<div class="center section">— PAGAMENTO —</div>
${(() => {
  // Pagamentos lançados (log passado, o gravado na mesa ou a divisão do pedido), depois a forma única.
  const pgs = conta.pagamentos;
  if (pgs.length === 0) return `<div style="font-weight:bold;">PAGAMENTO: ${formaPagamento}</div>`;
  const titulo = pgs.length === 1 ? 'PAGAMENTO:' : `PAGAMENTOS (${pgs.length}x):`;
  let body = `<div style="font-weight:bold;">${titulo}</div>`;
  let totalTroco = 0;
  body += pgs.map(pg => {
    const label = (pg.label || nomeFormaAgora(pg.method) || '?').toUpperCase();
    let line = `<div style="display:flex;justify-content:space-between;font-weight:bold;font-size:13px;margin-top:2px;"><span>${label}</span><span>R$ ${fmtMoeda(pg.valor)}</span></div>`;
    if (pg.troco) {
      totalTroco += pg.troco;
      const recebido = pg.recebido ? ` (recebido R$ ${fmtMoeda(pg.recebido)})` : '';
      line += `<div style="font-size:12px;font-weight:bold;margin-left:8px;">Troco: R$ ${fmtMoeda(pg.troco)}${recebido}</div>`;
    } else if (pg.recebido && pg.recebido === pg.valor) {
      line += `<div style="font-size:12px;font-weight:bold;margin-left:8px;color:#333;">Sem troco</div>`;
    }
    return line;
  }).join('');
  if (pgs.length > 1) {
    body += `<div style="display:flex;justify-content:space-between;font-weight:bold;font-size:13px;border-top:1px dashed #000;margin-top:4px;padding-top:3px;"><span>SOMA</span><span>R$ ${fmtMoeda(conta.ja_pago)}</span></div>`;
  }
  if (totalTroco > 0 && pgs.length > 1) {
    body += `<div style="display:flex;justify-content:space-between;font-size:12px;font-weight:bold;margin-top:2px;"><span>Troco total</span><span>R$ ${fmtMoeda(totalTroco)}</span></div>`;
  }
  body += conta.falta_pagar > 0.004
    ? `<div style="display:flex;justify-content:space-between;font-weight:bold;font-size:15px;margin-top:4px;"><span>FALTA PAGAR</span><span>R$ ${fmtMoeda(conta.falta_pagar)}</span></div>`
    : `<div style="font-weight:bold;font-size:15px;margin-top:4px;" class="center">PAGO</div>`;
  return body;
})()}
<div class="line"></div>
<div class="center section">— CLIENTE —</div>
<div style="font-weight:bold;font-size:16px;">CLIENTE: ${mesa.cliente || "—"}</div>
<div class="line"></div>
<div class="center" style="font-size:11px;font-weight:bold;color:#333;">NÃO É DOCUMENTO FISCAL</div>
<div class="center" style="font-size:11px;font-weight:bold;color:#333;margin-top:6px;">Sistema NÚCLEO - O Centro da sua gestão</div>
</body></html>`;

  // Regras de impressão (impressaoConfig ja carregado acima)
  const printerNames = impressaoConfig.fechamento || [];

  const modulo = (pedidos || []).find(p => p.modulo)?.modulo || "Mesas";
  const empresaData = buildEmpresaPayload(company, logoUrl);
  const somaDescontos = conta.descontos.reduce((s, d) => s + d.valor, 0);
  const pedidoForPrint = {
    ...(firstPedido || lastPedido || {}),
    codigo: firstPedido?.codigo || lastPedido?.codigo || mesa.codigo,
    items: mergedItems,
    // Compatibilidade com o app e o servidor sem `conta_cupom`: total LÍQUIDO,
    // descontos somados (os do primeiro pedido não entram de novo) e pagamentos reais
    total: totalFinal,
    desconto_manual: Math.round(somaDescontos * 100) / 100,
    desconto_tipo: "fixed",
    desconto_percentual: null,
    cupom_desconto: 0,
    cashback_usado: 0,
    pagamentos: conta.pagamentos.map((p) => ({ label: p.label || nomeFormaAgora(p.method), amount: p.valor, troco_precisa: p.troco > 0, troco_valor: p.troco, cash_received: p.recebido })),
    conta_cupom: { ...contaParaServidor(conta), pagamentos: conta.pagamentos.map((p) => ({ ...p, label: p.label || nomeFormaAgora(p.method) })) },
    is_fechamento: true,
    mesa_numero: mesa.numero,
    mesa_nome: mesa.nome || null,  // backend usa pra extrair apelido (Mesa 1A -> 1A)
    cliente_nome: mesa.cliente || firstPedido?.cliente_nome,
    forma_pagamento: formaPagamento,
  };
  return smartPrint(html, "CAIXA", printerNames, pedidoForPrint, empresaData, modulo, { is_fechamento: true, ...(opcoes.automatico ? { automatico: true } : {}) });
}
